import { Component, ElementRef, HostBinding, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { SitePagesService } from 'src/app/shared/api/site-pages.service';
import { CultureService } from 'src/app/shared/culture/culture.service';
import { rememberLanguage } from 'src/app/shared/culture/culture-switch.component';
import { SiteLanguage, SitePageChild, SiteTranslation } from 'src/app/shared/interfaces/site-page';
import { RootService } from 'src/app/shared/services/root.service';
import { SiteLanguageService } from 'src/app/shared/services/site-language.service';
import { TalkService } from 'src/app/shared/components/talk-dialog/talk-dialog.component';
import { UserService } from 'src/app/shared/services/user.service';

/// The feature pages under /primecommerce, in the order the menu lists them; one is listed only while it is published.
const FEATURE_PAGES = [
    'primecommerce/inventory',
    'primecommerce/couriers',
    'primecommerce/returns',
    'primecommerce/purchasing',
    'primecommerce/reports',
    'primecommerce/channels',
    'primecommerce/orders',
    'primecommerce/developers',
];

/// The one thin bar of the seller pages (/sell, /primecommerce, /tools): the shopper's header - topbar, logo-and-search row,
/// category chooser - is for shoppers, so a seller reading about PrimeCommerce gets the logo and the seller pages on the left,
/// and on the right the way back to the marketplace, Talk to us, the language, the account and Start free (the owner,
/// 2026-11-02). It replaces what the PrimeCommerce pages' own white menu (PcSubnavComponent) did under the big header.
@Component({
    selector: 'app-seller-bar',
    templateUrl: './seller-bar.component.html',
    styleUrls: ['./seller-bar.component.scss']
})
export class SellerBarComponent implements OnInit, OnDestroy {
    features: SitePageChild[] = [];
    isLoggedIn = false;
    open = false;
    /// Pulled away while the page is read downwards and back at the first scroll up, as the shop's own header is.
    @HostBinding('class.sbar-host--hidden') hidden = false;
    private scrollPosition = 0;
    private scrollDistance = 0;
    /// The bare path of the page on screen, without a leading slash ("primecommerce/pricing"): to mark where the reader is.
    here = '';

    /// The versions of the page on screen (SiteLanguageService), for the language switch.
    private translations: SiteTranslation[] = [];
    private destroy$ = new Subject<void>();

    constructor(private talk: TalkService, 
        @Inject(PLATFORM_ID) private platformId: any,
        private pages: SitePagesService,
        private cultures: CultureService,
        private languages: SiteLanguageService,
        private router: Router,
        private user: UserService,
        private host: ElementRef,
        public root: RootService,
    ) { }

    ngOnInit(): void {
        const lang: SiteLanguage = this.cultures.lang === 'bn' ? 'bn' : 'en';
        const overview = lang === 'en' ? this.pages.page('primecommerce', 'en') : this.pages.page('primecommerce', lang).pipe(
            catchError(error => error instanceof HttpErrorResponse && error.status === 404 ? this.pages.page('primecommerce', 'en') : throwError(error)));
        overview.pipe(takeUntil(this.destroy$)).subscribe(page => {
            const children = (page && page.children) || [];
            this.features = FEATURE_PAGES
                .map(key => children.filter(child => child.key === key)[0])
                .filter(child => !!child);
        }, () => this.features = []);


        this.languages.translations$.pipe(takeUntil(this.destroy$)).subscribe(translations => this.translations = translations);
        this.user.onUserInfoChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => this.isLoggedIn = this.user.isLoggedIn());

        this.markHere();
        this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.open = false;
                this.markHere();
            }
        });
    }

    private markHere(): void {
        this.here = this.cultures.bare(this.router.url).split(/[?#]/)[0].replace(/^\/+|\/+$/g, '').toLowerCase();
    }

    isCurrent(key: string): boolean {
        return this.here === key;
    }

    /// The feature page's address in the reader's language, or null while it is not published.
    pathOf(key: string): string | null {
        const child = this.features.filter(f => f.key === key)[0];
        return child ? child.path : null;
    }

    toggle(): void {
        this.open = !this.open;
    }

    /// The switch to the other language, as the topbar's: this page's own version in it where it has one, else this page's
    /// address in that language; a plain link, a full page load (Multilang_Culture_Spec.md 5.1).
    get languageSwitch(): { label: string, url: string, lang: string } {
        const to = this.languages.lang === 'bn' ? 'en' : 'bn';
        const here = this.cultures.urlIn(to, this.router.url);
        const version = this.translations.filter(t => t.language === to)[0];
        const elsewhere = version && this.cultures.bare(version.path) !== this.cultures.bare(this.router.url.split(/[?#]/)[0]);
        const url = elsewhere ? version.path
            : !version && this.translations.length > 0 && to === 'en' ? '/'
            : here;
        return to === 'bn'
            ? { label: 'বাংলা', url, lang: 'bn' }
            : { label: 'English', url, lang: 'en' };
    }

    chooseLanguage(code: string): void {
        rememberLanguage(document, code);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.open && !this.host.nativeElement.contains(event.target)) {
            this.open = false;
        }
    }

    @HostListener('window:scroll')
    onScroll(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        const position = window.pageYOffset;
        const delta = position - this.scrollPosition;
        this.scrollPosition = position;
        // The distance restarts when the direction changes.
        if ((delta < 0) !== (this.scrollDistance < 0)) {
            this.scrollDistance = 0;
        }
        this.scrollDistance += delta;
        if (position <= 56 || this.scrollDistance <= -10) {
            this.setHidden(false);
        } else if (this.scrollDistance >= 25) {
            this.open = false;
            this.setHidden(true);
        }
    }

    /// The in-page menu sticks under the bar while it shows (`--sbar-offset` on the page), and at the top of the window while it does not.
    private setHidden(hidden: boolean): void {
        this.hidden = hidden;
        document.documentElement.style.setProperty('--sbar-offset', hidden ? '0px' : '56px');
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.open = false;
    }

    openTalk(event: Event): void {
        event.preventDefault();
        this.talk.open();
    }

    ngOnDestroy(): void {
        if (isPlatformBrowser(this.platformId)) {
            document.documentElement.style.removeProperty('--sbar-offset');
        }
        this.destroy$.next();
        this.destroy$.complete();
    }
}
